import { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Search, Award, Download } from 'lucide-react';
import { toast } from 'sonner';

// Uses the getAdminEmployeeProgress endpoint to show certificates across all employees
import { getAdminEmployeeProgress, GetAdminEmployeeProgressOutputType } from 'zite-endpoints-sdk';

type Employee = GetAdminEmployeeProgressOutputType['employees'][0];

export default function CertificatesSection() {
  const [data, setData] = useState<GetAdminEmployeeProgressOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getAdminEmployeeProgress({})
      .then(setData)
      .catch(() => toast.error('Failed to load certificate data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-48" />
      {[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  // Flatten to cert-like rows from completed enrollments
  const certRows = (data?.employees || []).flatMap(emp =>
    emp.enrollments
      .filter(e => e.hasCertificate || e.status === 'Course Complete')
      .map(e => ({
        id: e.id,
        employeeName: emp.name,
        courseTitle: e.courseTitle,
        courseCode: e.courseCode,
        status: e.status,
        completedDate: e.completedDate,
        hasCertificate: e.hasCertificate,
      }))
  );

  const filtered = certRows.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.employeeName.toLowerCase().includes(q) || c.courseTitle.toLowerCase().includes(q);
  });

  return (
    <div>
      <h2 className="text-lg font-bold text-foreground flex items-center gap-2 mb-4">
        <Award className="w-5 h-5" /> Certificates ({certRows.length})
      </h2>

      <div className="relative mb-4">
        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
        <Input placeholder="Search by employee or course…" value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 text-sm" />
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="py-12 text-center">
            <Award className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No certificates issued yet</p>
            <p className="text-xs text-muted-foreground mt-1">Certificates are generated when employees complete courses and sign declarations</p>
          </div>
        ) : filtered.map(c => (
          <div key={c.id} className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl">
            <Award className={`w-8 h-8 shrink-0 ${c.hasCertificate ? 'text-primary' : 'text-muted-foreground'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{c.employeeName}</p>
              <p className="text-xs text-muted-foreground truncate">{c.courseTitle} · {c.courseCode}</p>
              <div className="flex items-center gap-2 mt-0.5">
                {c.hasCertificate ? (
                  <span className="text-[10px] font-bold uppercase bg-primary/15 text-primary px-1.5 py-0.5 rounded">Certificate Issued</span>
                ) : (
                  <span className="text-[10px] font-bold uppercase bg-muted text-muted-foreground px-1.5 py-0.5 rounded">Awaiting Generation</span>
                )}
                {c.completedDate && (
                  <span className="text-[10px] text-muted-foreground">Completed: {new Date(c.completedDate).toLocaleDateString('en-GB')}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
